import React from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "configureStore";
import { Card, Text } from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import {
  setModalxComponent,
  setModalxComponentProps,
} from "components/modalx/modalx.slice";
import Loading from "components/loading/Loading";
import { fetchOnboardingsByUser } from "services/onboarding.service";
import { STEP_LABELS, getOnboardingStatusLabel } from "utils/onboardingStatus";
import AnnulledBadge from "components/document-annulment/AnnulledBadge";
import { API_URL } from "../../constants";

// On a phone the table turns into cards: every `max-md:` class below belongs to that. A document
// is a block with its name on top, then its status and the two buttons on one line.
const DOCUMENT_BUTTON =
  "px-2.5 py-1 bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 text-xs font-medium max-md:min-h-11 max-md:px-4 max-md:text-sm";
const CONTAINER_BUTTON =
  "px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-xs font-medium max-md:min-h-11 max-md:text-sm";

/** Onboarding documents list */
export default function OnboardingDocuments({
  companyId,
  userId,
}: {
  companyId: string;
  userId: string;
}) {
  const dispatch: AppDispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const lang = ((i18n as any).language || "en") as "lt" | "en";
  const [onboardings, setOnboardings] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const data = await fetchOnboardingsByUser(companyId, userId);
        setOnboardings(data || []);
      } catch {
        setOnboardings([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [companyId, userId]);

  if (loading && onboardings.length === 0) {
    return <Loading />;
  }

  if (onboardings.length === 0) {
    return (
      <Card roundedAbove="sm">
        <Text as="p" variant="bodyMd">
          {t("no_onboarding_documents")}
        </Text>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {onboardings.map((ob: any) => {
        const stepGroups: Record<string, any[]> = {};
        for (const doc of ob.documents || []) {
          if (doc.fileType === "step_2_signed_asice") continue;
          const match = doc.fileType?.match(/^(step_\d+)/);
          const stepKey = match ? match[1] : "other";
          if (!stepGroups[stepKey]) stepGroups[stepKey] = [];
          stepGroups[stepKey].push(doc);
        }
        const groupKeys = Object.keys(stepGroups).sort();
        const asiceDoc = (ob.documents || []).find((d: any) => d.fileType === "step_2_signed_asice");

        return (
          <div key={ob.id} className="bg-white rounded-lg border border-gray-200 p-5 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-x-3 mb-3">
              <h2 className="text-base font-semibold">
                {ob.firstName} {ob.lastName} — {getOnboardingStatusLabel(ob.status, t, lang)}
              </h2>
              <span className="text-sm text-gray-500">{new Date(ob.createdAt).toLocaleDateString("lt-LT")}</span>
            </div>

            {groupKeys.length === 0 && !asiceDoc ? (
              <p className="text-sm text-gray-500">{t("no_documents")}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="text-sm w-auto max-w-full max-md:block max-md:w-full">
                  <thead className="max-md:hidden">
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-1.5 pr-6 font-medium text-gray-500">{t("status")}</th>
                      <th className="text-left py-1.5 pr-6 font-medium text-gray-500">{t("document")}</th>
                      <th className="text-left py-1.5 font-medium text-gray-500">{t("actions")}</th>
                    </tr>
                  </thead>
                  <tbody className="max-md:block">
                    {groupKeys.map((stepKey) => {
                      const files = stepGroups[stepKey];
                      const stepLabel = STEP_LABELS[stepKey]?.[lang] || STEP_LABELS[stepKey]?.en || stepKey;
                      const isStep2Group = stepKey === "step_2";
                      return (
                        <React.Fragment key={stepKey}>
                          <tr className="max-md:block">
                            <td colSpan={3} className="pt-3 pb-1 max-md:block">
                              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{stepLabel}</span>
                            </td>
                          </tr>
                          {files.map((doc: any, idx: number) => (
                            <tr
                              key={doc.id}
                              className={`${idx % 2 === 0 ? "bg-gray-50" : "bg-white"} max-md:flex max-md:flex-wrap max-md:items-center max-md:gap-x-3 max-md:gap-y-2 max-md:px-3 max-md:py-2`}
                            >
                              <td className="py-2 pr-4 max-md:py-0 max-md:pr-0">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${doc.file.isSigned ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"}`}>
                                  {doc.file.isSigned ? t("signed") : t("pending")}
                                </span>
                              </td>
                              <td className="py-2 pr-4 break-words max-md:order-first max-md:basis-full max-md:py-0 max-md:pr-0">
                                {doc.file.name || doc.file.title || doc.fileType}
                                {doc.file.annulledAt && (
                                  <>
                                    {" "}
                                    <AnnulledBadge />
                                  </>
                                )}
                              </td>
                              <td className="py-2 max-md:ml-auto max-md:py-0">
                                <div className="flex items-center gap-2">
                                  <button className={DOCUMENT_BUTTON} onClick={() => {
                                    dispatch(setModalxComponent("PdfView"));
                                    dispatch(setModalxComponentProps({ id: doc.file.id, companyId }));
                                  }}>
                                    {t("view")}
                                  </button>
                                  <button className={DOCUMENT_BUTTON} onClick={() => window.open(`${API_URL}/${companyId}/files/download/${doc.file.id}`, "_blank")}>
                                    {t("download")}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                          {isStep2Group && asiceDoc && (
                            <tr>
                              <td colSpan={3} className="py-2">
                                <button className={CONTAINER_BUTTON} onClick={() => window.open(`${API_URL}/${companyId}/files/download/${asiceDoc.file.id}`, "_blank")}>
                                  {t("download_signed_container")}
                                </button>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                    {asiceDoc && !groupKeys.includes("step_2") && (
                      <>
                        <tr>
                          <td colSpan={3} className="pt-3 pb-1">
                            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{STEP_LABELS.step_2?.[lang] || "Employee Forms"}</span>
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3} className="py-2">
                            <button className={CONTAINER_BUTTON} onClick={() => window.open(`${API_URL}/${companyId}/files/download/${asiceDoc.file.id}`, "_blank")}>
                              {t("download_signed_container")}
                            </button>
                          </td>
                        </tr>
                      </>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
